import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { format } from 'date-fns';
import { shallowEqual, useSelector } from 'react-redux';
import { selectNominatePeers } from 'selectors/nominatePeers';

import NoDataImg from 'assets/assetsV1/illustrations/NoReviewsV1.svg';

import { PEER_NOMINATION_COHORTS } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';

import { Divider } from 'uiToolkitV1/Divider';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import EmployeeBlock from './employeeBlock';
import SearchPeers from './searchPeers';

const getLabelText = ({ placeholderText = '', min, max, cohortKey }) => {
  if (cohortKey === PEER_NOMINATION_COHORTS.DLM) {
    return 'Nominate Dotted Line Manager';
  }
  let text = 'Nominate ';
  if (!min) {
    // covers both null and 0, basically means there's no minimum required
    text += `up to ${max} ${placeholderText}`;
  } else {
    text += `${min} to ${max} ${placeholderText}`;
  }

  if (
    [PEER_NOMINATION_COHORTS.SENIORS, PEER_NOMINATION_COHORTS.OUTSIDE_TEAM, PEER_NOMINATION_COHORTS.JUNIORS].includes(
      cohortKey
    )
  ) {
    if (max > 1) {
      text += ' Peers';
    } else {
      text += ' Peer';
    }
  }

  if (
    (cohortKey === PEER_NOMINATION_COHORTS.PRIMARY_TEAM || cohortKey === PEER_NOMINATION_COHORTS.ALL_ORG) &&
    max > 1
  ) {
    text += 's'; // plural in case of Team Peer
  }
  return text;
};

const SUB_HEADING_TEXT_MAP = {
  [PEER_NOMINATION_COHORTS.ALL_ORG]: 'Nominate people you worked closely with on your projects',
  [PEER_NOMINATION_COHORTS.DLM]: 'Nominate cross functional managers you collaborated with in this cycle',
  [PEER_NOMINATION_COHORTS.JUNIORS]:
    'Search for team members you worked with this cycle and nominate them as peer reviewers',
  [PEER_NOMINATION_COHORTS.OUTSIDE_TEAM]:
    'Search for internal stakeholders you worked with this cycle and nominate them as peer reviewers',
  [PEER_NOMINATION_COHORTS.PRIMARY_TEAM]:
    'Search for cross-functional colleagues you worked with this cycle and nominate them as peer reviewers',
  [PEER_NOMINATION_COHORTS.SENIORS]:
    'Search for leaders you worked with in this cycle and nominate them as peer reviewers',
};

const NominationStat = ({ min, nominatedCount }) => {
  const isRequiredSatisfied = nominatedCount >= min;
  return (
    <TagColored color={isRequiredSatisfied ? 'green' : 'red'}>
      {isRequiredSatisfied ? 'Complete' : 'Pending'}
    </TagColored>
  );
};

const EMPTY_TEXT_MAP = {
  [PEER_NOMINATION_COHORTS.ALL_ORG]: {
    heading: 'Nominate your peers',
    description: 'Search for your peers you worked with this cycle & nominate them to review your work',
  },
  [PEER_NOMINATION_COHORTS.DLM]: {
    heading: 'Nominate Dotted Line Managers',
    description: 'Search for dotted line manager you worked with this cycle & nominate them to review your work',
  },
  [PEER_NOMINATION_COHORTS.JUNIORS]: {
    heading: 'Nominate team members you collaborated with this cycle',
  },
  [PEER_NOMINATION_COHORTS.OUTSIDE_TEAM]: {
    heading: 'Nominate internal clients you collaborated with this cycle',
  },
  [PEER_NOMINATION_COHORTS.PRIMARY_TEAM]: {
    heading: 'Nominate team peers you collaborated with this cycle',
  },
  [PEER_NOMINATION_COHORTS.SENIORS]: {
    heading: 'Nominate leaders you collaborated with this cycle',
  },
};

export const PLACEHOLDER_TEXT = {
  JUNIORS: 'Direct Team Peers',
  SENIORS: 'Leaders',
  PRIMARY_TEAM: 'Peers',
  OUTSIDE_TEAM: 'Internal Clients',
  ALL_ORG: 'Peer Nomination',
  DLM: 'Dotted Line Managers',
};

export const NominationSection = ({
  nominatedCount = 0,
  min = 0,
  max,
  placeholderText,
  searchPlaceholderText,
  nominatedEmployees = [],
  removeNomination,
  cohortKey,
  empId,
  reviewId,
  allNominated,
  exclusions,
  recommendedPeers = [],
  nominatePeer,
  isNominationLimitApplicable = true,
}) => {
  const { nominationEndDateTS } = useSelector(selectNominatePeers, shallowEqual);
  return (
    <Box py={3} px={2} borderRadius="8px" bg="white" mb={1}>
      <Flex alignItems="center">
        <Box flexGrow={1} py="1" px={3}>
          <TextBodyMedium bold>{getLabelText({ placeholderText, min, max, nominatedCount, cohortKey })}</TextBodyMedium>
          <TextLabel>{SUB_HEADING_TEXT_MAP[cohortKey]}</TextLabel>
        </Box>
        {isNominationLimitApplicable && (
          <Box py="1" px={3}>
            <NominationStat min={min} max={max} nominatedCount={nominatedCount} />
          </Box>
        )}
      </Flex>
      <Divider my={3} borderColor="#CFD7DF" />
      <Box px={3} pb={4}>
        <SearchPeers
          placeholderText={searchPlaceholderText}
          allNominated={allNominated}
          exclusions={exclusions}
          nominatePeer={nominatePeer}
          cohortKey={cohortKey}
          empId={empId}
          reviewId={reviewId}
          recommendedPeers={recommendedPeers}
        />
        {nominatedEmployees.length === 0 && (
          <EmptyScreen
            illustrationStyle={{ width: '110px', margin: 'auto', marginTop: '12px' }}
            heading={
              EMPTY_TEXT_MAP[cohortKey].heading +
              (nominationEndDateTS ? ` before ${format(new Date(nominationEndDateTS), 'd MMM')}` : '')
            }
            description={EMPTY_TEXT_MAP[cohortKey].description}
            illustrationSrc={NoDataImg}
            isSmallTextSize
          />
        )}
        {nominatedEmployees.length > 0 && (
          <Box mt={5}>
            {nominatedEmployees.map((details, index) => (
              <Box mt={2}>
                <EmployeeBlock
                  key={details.peerProfile.employee.id}
                  employee={details.peerProfile.employee}
                  removeNomination={removeNomination}
                  details={details}
                />
                {isMobileView() && index < nominatedEmployees.length - 1 && <Divider my={3} borderColor="#E7E7E7" />}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
